import { useState, useMemo, ReactNode } from 'react'
import {
    Box,
    Typography,
    Collapse,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TextField,
    InputAdornment,
} from '@mui/material'
import {
    ExpandMore as ExpandMoreIcon,
    Search as SearchIcon,
    Close as CloseIcon,
} from '@mui/icons-material'

export interface CompactColumn {
    key: string
    label: string
    align?: 'left' | 'right' | 'center'
    /** Custom cell renderer; falls back to `row[key]`. */
    render?: (row: any) => ReactNode
    /** Value used for sorting (e.g. raw ms for a formatted timestamp). */
    sortValue?: (row: any) => any
    /** Set false to make the column header non-sortable (default sortable). */
    sortable?: boolean
    /** Include this column in the free-text search filter. */
    searchable?: boolean
    /** Plain string used for searching; falls back to `row[key]`. */
    searchValue?: (row: any) => string
    cellSx?: any
}

interface Props {
    columns: CompactColumn[]
    rows: any[]
    rowKey?: (row: any, i: number) => string
    defaultSort?: { key: string; dir: 'asc' | 'desc' }
    maxHeight?: number
    /** When set, renders a collapsible "title (count)" header above the table. */
    title?: string
    /** When set, rows become clickable (pointer cursor) and fire this on click. */
    onRowClick?: (row: any) => void
}

const headerCellSx = {
    color: 'text.secondary',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontSize: 10.5,
    fontWeight: 700,
    py: 0.75,
    borderBottom: '1px solid',
    borderColor: 'divider',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    bgcolor: 'background.paper',
}
const bodyCellSx = {
    fontSize: 12.5,
    py: 0.4,
    borderBottom: '1px solid',
    borderColor: 'rgba(255,255,255,0.05)',
}

// Compact, dense, sortable table. Sorting uses each column's `sortValue` so
// values sort by their real type (numbers numerically, timestamps by epoch ms)
// rather than the displayed string. Optionally collapsible.
const UniversalCompactTable = ({
    columns,
    rows = [],
    rowKey,
    defaultSort,
    maxHeight = 340,
    title,
    onRowClick,
}: Props) => {
    const [open, setOpen] = useState(!title)
    const [query, setQuery] = useState('')
    const [searchOpen, setSearchOpen] = useState(false)
    const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>(
        defaultSort || { key: columns[0]?.key, dir: 'asc' }
    )
    const searchableCols = useMemo(
        () => columns.filter((c) => c.searchable),
        [columns]
    )

    const sorted = useMemo(() => {
        const q = query.trim().toLowerCase()
        const filtered =
            q && searchableCols.length
                ? rows.filter((r) =>
                      searchableCols.some((c) =>
                          String(
                              (c.searchValue ? c.searchValue(r) : r[c.key]) ?? ''
                          )
                              .toLowerCase()
                              .includes(q)
                      )
                  )
                : rows
        const col = columns.find((c) => c.key === sort.key)
        if (!col) return filtered
        const get = col.sortValue || ((r: any) => r[col.key])
        const d = sort.dir === 'asc' ? 1 : -1
        return [...filtered].sort((a, b) => {
            const av = get(a)
            const bv = get(b)
            const an = typeof av === 'number' ? av : parseFloat(av)
            const bn = typeof bv === 'number' ? bv : parseFloat(bv)
            if (!Number.isNaN(an) && !Number.isNaN(bn)) return d * (an - bn)
            return d * String(av ?? '').localeCompare(String(bv ?? ''))
        })
    }, [rows, sort, columns, query, searchableCols])

    const toggleSort = (col: CompactColumn) => {
        if (col.sortable === false) return
        setSort((s) =>
            s.key === col.key
                ? { key: col.key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
                : { key: col.key, dir: 'asc' }
        )
    }
    const arrow = (k: string) =>
        sort.key === k ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''

    const table = (
        <TableContainer sx={{ maxHeight }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {columns.map((c) => (
                            <TableCell
                                key={c.key}
                                align={c.align}
                                sx={{
                                    ...headerCellSx,
                                    ...(c.sortable === false
                                        ? { cursor: 'default' }
                                        : {}),
                                }}
                                onClick={() => toggleSort(c)}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        justifyContent:
                                            c.align === 'right'
                                                ? 'flex-end'
                                                : 'flex-start',
                                    }}
                                >
                                    {!(c.searchable && searchOpen) && (
                                        <>
                                            {c.label}
                                            {c.sortable === false
                                                ? ''
                                                : arrow(c.key)}
                                        </>
                                    )}
                                    {c.searchable && (
                                        <SearchIcon
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                setSearchOpen((o) => !o)
                                            }}
                                            sx={{
                                                fontSize: 14,
                                                cursor: 'pointer',
                                                color: searchOpen
                                                    ? 'primary.main'
                                                    : 'inherit',
                                                opacity: searchOpen ? 1 : 0.65,
                                                '&:hover': { opacity: 1 },
                                            }}
                                        />
                                    )}
                                    {c.searchable && searchOpen && (
                                        <TextField
                                            variant="standard"
                                            autoFocus
                                            value={query}
                                            onChange={(e) =>
                                                setQuery(e.target.value)
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                            placeholder="Filter…"
                                            sx={{
                                                ml: 0.5,
                                                width: 150,
                                                '& .MuiInput-input': {
                                                    py: 0,
                                                    fontSize: 12,
                                                    fontWeight: 400,
                                                    textTransform: 'none',
                                                    letterSpacing: 0,
                                                },
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <CloseIcon
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                setQuery('')
                                                                setSearchOpen(
                                                                    false
                                                                )
                                                            }}
                                                            sx={{
                                                                fontSize: 14,
                                                                cursor: 'pointer',
                                                                color: 'text.secondary',
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                </Box>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sorted.map((r, i) => (
                        <TableRow
                            key={rowKey ? rowKey(r, i) : r.id ?? i}
                            onClick={
                                onRowClick ? () => onRowClick(r) : undefined
                            }
                            sx={{
                                '&:hover': { bgcolor: 'action.hover' },
                                cursor: onRowClick ? 'pointer' : 'default',
                            }}
                        >
                            {columns.map((c) => (
                                <TableCell
                                    key={c.key}
                                    align={c.align}
                                    sx={{ ...bodyCellSx, ...c.cellSx }}
                                >
                                    {c.render ? c.render(r) : r[c.key] ?? '—'}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )

    if (!title) return table
    return (
        <Box>
            <Box
                onClick={() => setOpen((o) => !o)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    cursor: 'pointer',
                    py: 1,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: '0.07em',
                        fontSize: 10.5,
                        fontWeight: 600,
                    }}
                >
                    {title} ({rows.length})
                </Typography>
                <ExpandMoreIcon
                    sx={{
                        fontSize: 18,
                        color: 'text.secondary',
                        transform: open ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s',
                    }}
                />
            </Box>
            <Collapse in={open} unmountOnExit>
                {table}
            </Collapse>
        </Box>
    )
}

export default UniversalCompactTable
